import { Tool } from '@procureai/agents';
import { useFetchClient } from '@procureai/uix';
import { useCallback, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { appConfig } from '~/common/config';

// seven-bridges (on @procureai/agents 27.x) still serves this tool under its old id; the shared
// `Tool` enum renamed it to `generateRequirementsDocument` (APP-7319), so it is spelled out here.
export const GENERATE_STATEMENT_OF_WORK_TOOL = 'generateStatementOfWork';

// Define the schema for each tool's input and output
type ToolsSchema = {
  [GENERATE_STATEMENT_OF_WORK_TOOL]: {
    input: { chatSessionId: string; language: string };
    output: { statementOfWork: string; missingSections: { section: string; information: string }[] };
  };
  [Tool.userMessageSuggestions]: {
    input: { id: string };
    output: { rawSuggestions: { label: string; message: string }[] };
  };
  // Add more tools here as needed
};
type ToolId = keyof ToolsSchema;

// type we are sending off to the backend
type ToolInput<K extends ToolId> = { data: ToolsSchema[K]['input']; resourceId: string };

/**
 * Custom hook for executing tool calls with authentication and loading state management.
 *
 * @template K - The tool identifier type that extends ToolId
 * @param toolId - The unique identifier for the tool to execute
 *
 * @returns An object containing:
 * - `mutate`: Async function that executes the tool with the provided input and returns the tool's output
 * - `isLoading`: Boolean indicating whether a tool execution is currently in progress
 *
 * @example
 * ```typescript
 * const { mutate, isLoading } = useToolCall('myToolId');
 * const result = await mutate(inputData);
 * ```
 *
 * @throws {Error} Rethrows any errors that occur during tool execution
 */
export function useToolCall<K extends ToolId>(toolId: K) {
  const fetchClient = useFetchClient();
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const mutate = useCallback(
    async (input: ToolsSchema[K]['input']): Promise<ToolsSchema[K]['output']> => {
      setIsLoading(true);
      try {
        const data = await fetchClient
          .post<ToolsSchema[K]['output']>(`/api/tools/${toolId}/execute`, {
            baseUrl: appConfig.sevenbridges.url,
            json: {
              data: input,
              resourceId: (auth.user?.profile.user_id as string) ?? '',
            } as ToolInput<K>,
          })
          .json();
        return data!;
      } catch (error: unknown) {
        throw error as Error;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchClient, toolId, auth.user],
  );

  return { mutate, isLoading };
}
